import { useState, useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { UserFocusItem } from '@shared/types';
import { subscribeToUserFocus, refreshUserContent } from '@shared/services/userContentService';
import { getLocalDateString } from '@shared/utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import { useDayChange } from '../../hooks/useDayChange';
import { logError } from '@/lib/log';

/** Account created within the last 2 minutes — almost certainly a first-time load. */
const isNewAccount = (creationTime: string | undefined): boolean => {
    if (!creationTime) return false;
    return Date.now() - new Date(creationTime).getTime() < 2 * 60 * 1000;
};

const isNative = Capacitor.isNativePlatform();

interface UseFocusItemsReturn {
    focusItems: UserFocusItem[];
    focusHasDocs: boolean;
    focusHasStaleContent: boolean;
    focusLoading: boolean;
    /** True while refreshUserContent() Cloud Function is in flight */
    contentRefreshing: boolean;
    /** True once the day's content pipeline has settled — subscription
     *  answered, refresh decision made, and any refreshUserContent flight
     *  (including one deferred to tab visibility) finished. See
     *  UserContextState.contentSettled. */
    contentSettled: boolean;
}

export const useFocusItems = (): UseFocusItemsReturn => {
    const { currentUser } = useAuth();
    const localDate = useDayChange(); // Own reactive state; triggers reset effect at midnight
    const [focusItems, setFocusItems] = useState<UserFocusItem[]>([]);
    const [focusHasDocs, setFocusHasDocs] = useState(false);
    const [focusHasStaleContent, setFocusHasStaleContent] = useState(false);
    const [focusLoading, setFocusLoading] = useState(true);
    const [contentBootstrapAttempted, setContentBootstrapAttempted] = useState(false);
    const [contentRefreshing, setContentRefreshing] = useState(false);

    // Ref-based concurrency guard: prevents multiple refreshUserContent calls
    // from firing simultaneously (e.g., eager + normal paths in the same cycle).
    // contentBootstrapAttempted (state) still controls day-change reset timing.
    const refreshInFlight = useRef(false);
    const eagerRefreshFired = useRef(false);

    // Web visibility tracking: on web, the day-change interval fires at midnight
    // even when the tab is hidden. We let the day change update the UI (clears
    // yesterday's completed state) but defer the Cloud Function call until the
    // user actually returns to the tab. Native apps don't need this — the OS
    // suspends the process when backgrounded.
    const [isTabVisible, setIsTabVisible] = useState(
        () => isNative || document.visibilityState === 'visible'
    );
    const [pendingRefresh, setPendingRefresh] = useState(false);

    useEffect(() => {
        if (isNative) return;
        const handler = () => setIsTabVisible(document.visibilityState === 'visible');
        document.addEventListener('visibilitychange', handler);
        return () => document.removeEventListener('visibilitychange', handler);
    }, []);

    // Reset content bootstrap flag on day change so stale content triggers refresh
    useEffect(() => {
        setContentBootstrapAttempted(false);
    }, [localDate]);

    // Guarded refresh: ensures only one refreshUserContent call at a time.
    // Sets contentBootstrapAttempted synchronously via the ref check, then
    // confirms via state so React re-renders pick up the new value.
    const guardedRefresh = useCallback(async (opts: Parameters<typeof refreshUserContent>[0]) => {
        if (refreshInFlight.current) return;
        refreshInFlight.current = true;
        setContentBootstrapAttempted(true);
        setPendingRefresh(false);
        setContentRefreshing(true);
        try {
            await refreshUserContent(opts);
        } finally {
            refreshInFlight.current = false;
            setContentRefreshing(false);
        }
    }, []);

    // After a new account is connected, recalculate grow targeting and variable
    // cache so videos, exercises, and chat reflect the new financial data.
    // Focus items are intentionally excluded — they're locked for the day.
    useEffect(() => {
        if (!currentUser) return;

        const handleConnectionSuccess = () => {
            guardedRefresh({
                contentTypes: [],
                forceGrowRetarget: true,
                localDate: getLocalDateString(),
                platform: Capacitor.getPlatform(),
            }).catch((error) => {
                logError(error, { context: 'refresh_targeting_after_account_connection' });
            });
        };

        window.addEventListener('connectionSuccess', handleConnectionSuccess);
        return () => window.removeEventListener('connectionSuccess', handleConnectionSuccess);
    }, [currentUser, guardedRefresh]);

    // Eager refresh for brand-new accounts: fire refreshUserContent immediately
    // without waiting for the Firestore subscription to confirm the collection is
    // empty. This eliminates the round-trip latency of: subscribe → query returns
    // empty → detect needsRefresh → call Cloud Function. The subscription still
    // runs in parallel and will pick up the docs once the Cloud Function writes them.
    useEffect(() => {
        if (!currentUser || eagerRefreshFired.current) return;
        if (!isNewAccount(currentUser.metadata.creationTime)) return;

        eagerRefreshFired.current = true;
        guardedRefresh({
            contentTypes: ['focus', 'videos', 'exercises'],
            seedDefaultsIfEmpty: true,
            localDate: getLocalDateString(),
            platform: Capacitor.getPlatform(),
        }).catch((error) => {
            logError(error, { context: 'eager_refresh_new_user' });
            // Allow the normal refresh path to retry
            setContentBootstrapAttempted(false);
        });
    }, [currentUser, guardedRefresh]);

    // Subscribe to userContent focus items.
    // Re-subscribe on day change so the snapshot callback re-evaluates todayUtc
    // (otherwise stale items from yesterday remain visible until a Firestore write
    // triggers the snapshot — which is what caused the "completed yesterday, shown
    // today" bug).
    useEffect(() => {
        if (!currentUser) {
            setFocusItems([]);
            setFocusHasDocs(false);
            setFocusHasStaleContent(false);
            setFocusLoading(false);
            return;
        }

        setFocusLoading(true);
        const unsubscribe = subscribeToUserFocus(currentUser.uid, (items, hasAnyDocs, hasStaleContent) => {
            setFocusItems(items);
            setFocusHasDocs(hasAnyDocs);
            setFocusHasStaleContent(hasStaleContent);
            setFocusLoading(false);
        });

        return () => {
            unsubscribe();
        };
    }, [currentUser, localDate]);

    // Reset content bootstrap flag when user changes
    useEffect(() => {
        setContentBootstrapAttempted(false);
        eagerRefreshFired.current = false;
    }, [currentUser?.uid]);

    // Refresh userContent: on first load (empty) or when content is stale (activeDate < today).
    // Focus items are locked per local day — this triggers replenishment for a new day.
    useEffect(() => {
        if (!currentUser || focusLoading || contentBootstrapAttempted) {
            return;
        }

        // Stale detection is now done in userContentService BEFORE filtering,
        // so we can properly detect items from previous days
        const needsRefresh = !focusHasDocs || focusHasStaleContent;

        if (!needsRefresh) {
            // Lock the bootstrap flag — no refresh needed today.
            setContentBootstrapAttempted(true);
            return;
        }

        // On web, defer the Cloud Function call until the tab is visible.
        // The day change already cleared yesterday's completed state from the UI,
        // so the user won't see stale celebration screens when they return.
        if (!isTabVisible) {
            setPendingRefresh(true);
            return;
        }

        guardedRefresh({
            contentTypes: ['focus', 'videos', 'exercises'],
            seedDefaultsIfEmpty: !focusHasDocs,
            localDate: getLocalDateString(),
            platform: Capacitor.getPlatform(),
        }).catch((error) => {
            logError(error, { context: 'refresh_user_content' });
        });
    }, [currentUser, focusLoading, focusHasDocs, focusHasStaleContent, contentBootstrapAttempted, isTabVisible, guardedRefresh]);

    // Fire the deferred refresh when the web tab becomes visible again.
    useEffect(() => {
        if (!pendingRefresh || !isTabVisible || !currentUser) return;

        guardedRefresh({
            contentTypes: ['focus', 'videos', 'exercises'],
            seedDefaultsIfEmpty: !focusHasDocs,
            localDate: getLocalDateString(),
            platform: Capacitor.getPlatform(),
        }).catch((error) => {
            logError(error, { context: 'refresh_user_content_deferred' });
        });
    }, [pendingRefresh, isTabVisible, currentUser, focusHasDocs, guardedRefresh]);

    // Settled = the focus subscription has answered, the refresh decision has
    // been made (contentBootstrapAttempted covers both "refresh started" and
    // "no refresh needed today"), nothing is deferred to tab visibility, and
    // no refreshUserContent call is in flight. Day/user changes reset
    // contentBootstrapAttempted, so this correctly drops back to false at
    // midnight until the new day's content is delivered.
    const contentSettled = !focusLoading && contentBootstrapAttempted
        && !pendingRefresh && !contentRefreshing;

    return {
        focusItems,
        focusHasDocs,
        focusHasStaleContent,
        focusLoading,
        contentRefreshing,
        contentSettled
    };
};
